import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';

import css from './SearchMapPriceLabel.module.css';

const TAP_MOVE_THRESHOLD_PX = 10;

class SearchMapPriceLabel extends Component {
  constructor(props) {
    super(props);
    this.touchStartCoords = null;
    this.suppressClick = false;
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTouchCancel = this.handleTouchCancel.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.stopMapInteraction = this.stopMapInteraction.bind(this);
  }

  activate(e) {
    e.preventDefault();
    e.stopPropagation();

    const currentListing = ensureListing(this.props.listing);
    this.props.onListingClicked(currentListing);
  }

  handleTouchStart(e) {
    e.stopPropagation();
    this.props.onLabelInteractionStart?.();

    const touch = e.touches[0];
    if (touch) {
      this.touchStartCoords = { x: touch.clientX, y: touch.clientY };
    }
  }

  handleTouchEnd(e) {
    e.stopPropagation();
    this.props.onLabelInteractionEnd?.();

    const touch = e.changedTouches[0];
    if (!touch || !this.touchStartCoords) {
      return;
    }

    const dx = touch.clientX - this.touchStartCoords.x;
    const dy = touch.clientY - this.touchStartCoords.y;
    this.touchStartCoords = null;

    if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
      return;
    }

    e.preventDefault();
    this.suppressClick = true;
    window.setTimeout(() => {
      this.suppressClick = false;
    }, 450);

    this.activate(e);
  }

  handleTouchCancel(e) {
    e.stopPropagation();
    this.touchStartCoords = null;
    this.props.onLabelInteractionEnd?.();
  }

  handleClick(e) {
    if (this.suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    this.activate(e);
  }

  stopMapInteraction(e) {
    e.stopPropagation();
  }

  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      className,
      rootClassName,
      intl,
      listing,
      isActive,
      config,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price } = currentListing.attributes;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      price && price.currency === config.currency ? formatMoney(intl, price) : price.currency;

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, { [css.priceLabelActive]: isActive });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });
    let rmvChars = formattedPrice.replace(/[^0-9\.,]/g, '');
    let replaceX = rmvChars.replace(/[.,]/g, '');
    let numberHolder = Number(replaceX) / 100;
    let roundedPrice = Math.ceil(numberHolder);
    let finalPrice = `${roundedPrice}€`;

    return (
      <button
        className={classes}
        type="button"
        onClick={this.handleClick}
        onTouchStart={this.handleTouchStart}
        onTouchEnd={this.handleTouchEnd}
        onTouchCancel={this.handleTouchCancel}
        onMouseDown={this.stopMapInteraction}
        data-elbaction={`click:click destp-maplist-${currentListing?.attributes?.title}-uuid-${currentListing?.id?.uuid}`}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{finalPrice}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

SearchMapPriceLabel.defaultProps = {
  className: null,
  rootClassName: null,
};

SearchMapPriceLabel.propTypes = {
  className: string,
  rootClassName: string,
  listing: propTypes.listing.isRequired,
  onListingClicked: func.isRequired,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SearchMapPriceLabel);
